import { useState, useMemo } from 'react'
import { Segment, PlanItem } from '../../types'
import { AddPlanItemDialog } from './AddPlanItemDialog'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Plus, Trash2, Mountain, Clock, CheckCircle, XCircle } from 'lucide-react'
import { useDeletePlanItem } from '../../hooks/usePlanItems'
import { formatPace, formatTime } from '../../lib/calculations'
import { calculateSegmentGoalStatus } from '../../lib/nutrition'

interface SegmentPlanBlockProps {
  segment: Segment
  segmentIndex: number
  planItems: PlanItem[]
  cumulativeDistance: number
  pace: number
  segmentTime: number
  cumulativeTime: number
  nutritionTargets?: {
    carbs_g_per_h: { low: number; high: number }
    fluids_ml_per_h: { low: number; high: number }
    sodium_mg_per_h: { low: number; high: number }
  }
}

export function SegmentPlanBlock({
  segment,
  segmentIndex,
  planItems,
  cumulativeDistance,
  pace,
  segmentTime,
  cumulativeTime,
  nutritionTargets
}: SegmentPlanBlockProps) {
  const [showAddDialog, setShowAddDialog] = useState(false)
  const deleteMutation = useDeletePlanItem()

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this plan item?')) {
      await deleteMutation.mutateAsync(id)
    }
  }

  const formatNutrition = (item: PlanItem) => {
    const parts = []
    if (item.carbs_g) parts.push(`${item.carbs_g}g carbs`)
    if (item.fluids_ml) parts.push(`${item.fluids_ml}ml fluids`)
    if (item.sodium_mg) parts.push(`${item.sodium_mg}mg sodium`)
    if (item.caffeine_mg) parts.push(`${item.caffeine_mg}mg caffeine`)
    return parts.join(', ')
  }

  // Calculate segment nutrition totals
  const segmentTotals = useMemo(() => ({
    carbs: planItems.reduce((sum, item) => sum + (item.carbs_g || 0), 0),
    fluids: planItems.reduce((sum, item) => sum + (item.fluids_ml || 0), 0),
    sodium: planItems.reduce((sum, item) => sum + (item.sodium_mg || 0), 0),
    caffeine: planItems.reduce((sum, item) => sum + (item.caffeine_mg || 0), 0)
  }), [planItems])

  // Calculate goal status if targets are provided
  const goalStatus = useMemo(() => {
    if (!nutritionTargets) return null

    return calculateSegmentGoalStatus(
      segmentTime,
      {
        carbs_g: segmentTotals.carbs,
        fluids_ml: segmentTotals.fluids,
        sodium_mg: segmentTotals.sodium,
        caffeine_mg: segmentTotals.caffeine
      },
      nutritionTargets
    )
  }, [segmentTime, segmentTotals, nutritionTargets])

  // Helper function to get status icon and color
  const getStatusDisplay = (status: 'low' | 'optimal' | 'high') => {
    switch (status) {
      case 'low':
        return { icon: XCircle, color: 'text-red-500', bgColor: 'bg-red-50', borderColor: 'border-red-200' }
      case 'high':
      case 'optimal':
        return { icon: CheckCircle, color: 'text-green-500', bgColor: 'bg-green-50', borderColor: 'border-green-200' }
    }
  }

  return (
    <Card className="w-full border-l-4 border-l-primary-600 bg-primary-50 shadow-minimal">
      <CardHeader className="pb-4">
        <div className="flex justify-between items-start gap-4">
          <div className="flex-1 space-y-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl font-bold flex items-center gap-3 text-gray-900">
                <div className="flex items-center justify-center w-8 h-8 bg-blue-500 text-white rounded-full text-sm font-bold">
                  {segmentIndex + 1}
                </div>
                {segment.label || `Segment ${segmentIndex + 1}`}
              </CardTitle>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowAddDialog(true)}
                className="shrink-0 bg-white hover:bg-blue-50 border-blue-200 text-blue-700"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Nutrition
              </Button>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-3 bg-white/80 rounded-lg border border-blue-100">
              <div className="text-center">
                <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Distance</div>
                <div className="font-bold text-gray-900 flex items-center justify-center gap-1">
                  <Mountain className="h-4 w-4 text-blue-500" />
                  {segment.distance_km}km
                </div>
              </div>
              <div className="text-center">
                <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Elevation</div>
                <div className="font-bold text-gray-900">
                  {(segment.elev_gain_m || 0) > 0 || (segment.elev_loss_m || 0) > 0 ? (
                    <span>+{segment.elev_gain_m || 0}m / -{segment.elev_loss_m || 0}m</span>
                  ) : (
                    <span className="text-gray-400">Flat</span>
                  )}
                </div>
              </div>
              <div className="text-center">
                <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Pace</div>
                <div className="font-bold text-gray-900 flex items-center justify-center gap-1">
                  <Clock className="h-4 w-4 text-blue-500" />
                  {formatPace(pace)}
                </div>
              </div>
              <div className="text-center">
                <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Time</div>
                <div className="font-bold text-gray-900">{formatTime(segmentTime)}</div>
              </div>
            </div>
            
            <div className="flex items-center justify-between text-sm">
              <div className="text-gray-600">
                <span className="font-medium">Cumulative:</span> {cumulativeDistance.toFixed(1)}km
              </div>
              <div className="text-gray-600">
                <span className="font-medium">ETA:</span> T+{formatTime(cumulativeTime)}
              </div>
            </div>
            
            {(planItems.length > 0 || goalStatus) && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                {(segmentTotals.carbs > 0 || goalStatus) && (
                  <div className="text-center">
                    <div className={`text-xs font-medium ${goalStatus ? getStatusDisplay(goalStatus.carbs.status).color : 'text-blue-600'}`}>
                      {segmentTotals.carbs}g
                      {goalStatus && (
                        <span className="text-gray-500 font-normal">
                          {' / '}{Math.round(goalStatus.carbs.expected.low)}-{Math.round(goalStatus.carbs.expected.high)}g
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-blue-500">carbs</div>
                  </div>
                )}
                {(segmentTotals.fluids > 0 || goalStatus) && (
                  <div className="text-center">
                    <div className={`text-xs font-medium ${goalStatus ? getStatusDisplay(goalStatus.fluids.status).color : 'text-blue-600'}`}>
                      {segmentTotals.fluids}ml
                      {goalStatus && (
                        <span className="text-gray-500 font-normal">
                          {' / '}{Math.round(goalStatus.fluids.expected.low)}-{Math.round(goalStatus.fluids.expected.high)}ml
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-blue-500">fluids</div>
                  </div>
                )}
                {(segmentTotals.sodium > 0 || goalStatus) && (
                  <div className="text-center">
                    <div className={`text-xs font-medium ${goalStatus ? getStatusDisplay(goalStatus.sodium.status).color : 'text-blue-600'}`}>
                      {segmentTotals.sodium}mg
                      {goalStatus && (
                        <span className="text-gray-500 font-normal">
                          {' / '}{Math.round(goalStatus.sodium.expected.low)}-{Math.round(goalStatus.sodium.expected.high)}mg
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-blue-500">sodium</div>
                  </div>
                )}
                {segmentTotals.caffeine > 0 && (
                  <div className="text-center">
                    <div className="text-xs text-blue-600 font-medium">{segmentTotals.caffeine}mg</div>
                    <div className="text-xs text-blue-500">caffeine</div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </CardHeader>
      
      {planItems.length > 0 && (
        <CardContent className="pt-0">
          <div className="bg-white rounded-lg border border-blue-100 overflow-hidden">
            <div className="bg-blue-50 px-4 py-2 border-b border-blue-100">
              <h4 className="text-sm font-semibold text-blue-900">Nutrition Plan</h4>
            </div>
            <div className="p-3 space-y-3">
              {planItems.map((item) => (
                <div 
                  key={item.id} 
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-3 flex-1">
                    <Badge 
                      variant={item.type === 'snack' ? 'default' : item.type === 'drink' ? 'secondary' : 'outline'}
                      className="shrink-0"
                    >
                      {item.type}
                    </Badge>
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900">{item.name}</div>
                      {formatNutrition(item) && (
                        <div className="text-sm text-blue-600 font-medium mt-1">{formatNutrition(item)}</div>
                      )}
                      {item.note && (
                        <div className="text-sm text-gray-500 italic mt-1">"{item.note}"</div>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(item.id)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 shrink-0"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      )}

      {segment.run_id && (
        <AddPlanItemDialog
          runId={segment.run_id}
          preSelectedLocation={{
            type: 'segment',
            id: segment.id,
            label: segment.label || `Segment ${segmentIndex + 1}`
          }}
          open={showAddDialog}
          onOpenChange={setShowAddDialog}
        />
      )}
    </Card>
  )
}